import React, { useEffect, useState, useCallback } from "react";
import { Sunrise, Play, Loader2, Clock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";

const todayKey = (uid) => `fd_startday_${uid}_${new Date().toISOString().slice(0, 10)}`;

export default function StartDayPrompt() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [sel, setSel] = useState(new Set());
  const [starting, setStarting] = useState(false);

  const check = useCallback(async () => {
    if (!user?.id) return;
    if (localStorage.getItem(todayKey(user.id))) return;
    try {
      const { data } = await api.get("/tasks");
      const mine = (data || []).filter(
        (t) => (t.assigned_to === user.id || (t.created_by === user.id && !t.assigned_to)) && ["todo", "in_progress"].includes(t.status) && !t.timer_running
      );
      if (mine.length === 0) { localStorage.setItem(todayKey(user.id), "none"); return; }
      setTasks(mine.slice(0, 12));
      setSel(new Set(mine.filter((t) => t.status === "in_progress").map((t) => t.id)));
      setOpen(true);
    } catch { /* ignore */ }
  }, [user]);

  useEffect(() => { const t = setTimeout(check, 1500); return () => clearTimeout(t); }, [check]);

  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const dismiss = () => { if (user?.id) localStorage.setItem(todayKey(user.id), "later"); setOpen(false); };

  const startDay = async () => {
    setStarting(true);
    try {
      await Promise.all(Array.from(sel).map((id) => api.post(`/tasks/${id}/timer/start`).catch(() => {})));
      if (user?.id) localStorage.setItem(todayKey(user.id), "started");
      toast.success(sel.size > 0 ? `Chrono lancé sur ${sel.size} tâche(s). Bonne journée !` : "Bonne journée !");
      setOpen(false);
      window.dispatchEvent(new Event("fd-timers-changed"));
    } catch { toast.error("Impossible de lancer les chronos"); }
    finally { setStarting(false); }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && dismiss()}>
      <DialogContent className="max-w-md" data-testid="start-day-dialog">
        <DialogHeader>
          <DialogTitle className="font-cabinet flex items-center gap-2 text-xl">
            <span className="w-9 h-9 rounded-xl bg-amber-400/20 text-amber-400 flex items-center justify-center"><Sunrise size={18} /></span>
            Prêt à démarrer votre journée ?
          </DialogTitle>
          <DialogDescription>Sélectionnez la ou les tâches sur lesquelles vous travaillez maintenant — le chrono se lance automatiquement.</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5 max-h-[44vh] overflow-y-auto -mx-1 px-1">
          {tasks.map((t) => {
            const on = sel.has(t.id);
            return (
              <button key={t.id} data-testid={`startday-task-${t.id}`} onClick={() => toggle(t.id)}
                className={`w-full flex items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors ${on ? "border-klein bg-klein/10" : "border-white/10 hover:border-white/25 bg-background/40"}`}>
                <span className={`w-5 h-5 rounded-md border flex items-center justify-center shrink-0 ${on ? "bg-klein border-klein text-white" : "border-zinc-500"}`}>{on && <CheckCircle2 size={14} />}</span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{t.title}</div>
                  <div className="text-[11px] text-zinc-500 truncate">{t.ref}{t.company_name ? ` · ${t.company_name}` : ""}{t.status === "in_progress" ? " · en cours" : ""}</div>
                </div>
                {(t.time_spent_seconds > 0) && <span className="text-[11px] text-zinc-500 flex items-center gap-1 shrink-0"><Clock size={11} />{Math.round(t.time_spent_seconds / 60)}min</span>}
              </button>
            );
          })}
        </div>
        <DialogFooter className="flex items-center justify-between">
          <button data-testid="startday-later" onClick={dismiss} className="h-9 px-4 rounded-lg text-sm text-zinc-400 hover:text-white mr-auto">Plus tard</button>
          <button data-testid="startday-confirm" onClick={startDay} disabled={starting} className="h-9 px-4 rounded-lg bg-klein text-white text-sm font-semibold disabled:opacity-60 flex items-center gap-1.5">
            {starting ? <Loader2 size={15} className="animate-spin" /> : <Play size={15} />}
            {sel.size > 0 ? `Démarrer (${sel.size})` : "Démarrer la journée"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
